import type { Member, Project, TaskSummary } from "../shared/types";
import { TaskTypeBadge } from "./TaskClassification";
import { Button } from "./ui";
import { TaskField, TaskStatusIcon, dateLabel } from "./TaskFields";
import { useRef, useState } from "react";

export default function TaskList({
  tasks,
  project,
  members,
  currentUserId,
  mobile = false,
  onOpen,
  onPatch,
}: {
  tasks: TaskSummary[];
  project: Project;
  members: Member[];
  currentUserId: string;
  mobile?: boolean;
  onOpen: (id: string) => void;
  onPatch: (id: string, input: Record<string, unknown>) => Promise<unknown>;
}) {
  const readOnly = project.role === "viewer" || project.repo_archived;
  return (
    <div className="issue-table" aria-label="项目任务列表">
      <div className="issue-table-head" aria-hidden="true">
        <span>任务</span>
        <span>状态</span>
        <span>优先级</span>
        <span>处理人</span>
        <span>截止日期</span>
      </div>
      <div role="list">
        {tasks.map((task) => (
          <TaskRow
            key={task.id}
            task={task}
            project={project}
            members={members}
            currentUserId={currentUserId}
            mobile={mobile}
            readOnly={readOnly}
            onOpen={onOpen}
            onPatch={onPatch}
          />
        ))}
      </div>
    </div>
  );
}
function TaskRow({
  task,
  project,
  members,
  currentUserId,
  mobile,
  readOnly,
  onOpen,
  onPatch,
}: {
  task: TaskSummary;
  project: Project;
  members: Member[];
  currentUserId: string;
  mobile: boolean;
  readOnly: boolean;
  onOpen: (id: string) => void;
  onPatch: (id: string, input: Record<string, unknown>) => Promise<unknown>;
}) {
  const [busy, setBusy] = useState(false);
  const writing = useRef(false);
  const commit = async (input: Record<string, unknown>) => {
    if (writing.current) throw new Error("正在保存，请稍后再试");
    writing.current = true;
    setBusy(true);
    try {
      return await onPatch(task.id, {
        expected_revision: task.revision,
        ...input,
      });
    } finally {
      writing.current = false;
      setBusy(false);
    }
  };
  return (
    <article className="issue-row" role="listitem">
      <Button
        className="issue-title-button"
        onClick={() => onOpen(task.id)}
        aria-label={`打开任务 ${task.title}`}
      >
        <TaskStatusIcon status={task.status} />
        <span className="issue-title-content">
          <span className="issue-identity">
            {task.github_issue_number
              ? `#${task.github_issue_number}`
              : `T-${task.number}`}
            <TaskTypeBadge name={task.issue_type} />
          </span>
          <strong>{task.title}</strong>
          {task.labels.length ? (
            <span className="issue-row-labels">
              {task.labels.slice(0, 2).map((label) => (
                <span key={label}>{label}</span>
              ))}
              {task.labels.length > 2 ? (
                <span>+{task.labels.length - 2}</span>
              ) : null}
            </span>
          ) : null}
        </span>
      </Button>
      {(["status", "priority", "people"] as const).map((kind) => (
        <div className={`issue-cell cell-${kind}`} key={kind}>
          <TaskField
            task={task}
            project={project}
            members={members}
            currentUserId={currentUserId}
            mobile={mobile}
            kind={kind}
            onCommit={commit}
            disabled={readOnly || busy}
            compact
          />
        </div>
      ))}
      <div
        className={`issue-due ${task.due_date && task.due_date < new Date().toLocaleDateString("en-CA") && !["done", "cancelled"].includes(task.status) ? "overdue" : ""}`}
      >
        {task.due_date ? dateLabel(task.due_date) : "—"}
      </div>
    </article>
  );
}
